import React, { useState, useEffect, useRef } from 'react';
import {
  BarChart, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell, PieChart, AreaChart, Pie, Area, LabelList, Tooltip, Legend
} from 'recharts';
import { getRequest } from '../services/apiService';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import { Download, FileText } from 'lucide-react';

export const ProjectStatusChart = ({ selectedProject, selectedShip }) => {
  const [projectStatusData, setProjectStatusData] = useState([]);
  const [loading, setLoading] = useState(true);
  const chartRef = useRef(null);

  useEffect(() => {
    const fetchProjectStatus = async () => {
      setLoading(true);
      
      // Skip fetch if no ship is selected
      if (!selectedShip) {
        setLoading(false);
        setProjectStatusData([]);
        return;
      }

      try {
        // Add project_id query parameter using selectedShip
        const response = await getRequest(
          `/transaction/project-status/?project_id=${selectedShip}`
        );
        
        if (response && typeof response === 'object') {
          const transformedData = [
            { name: 'On Track', value: response['On Track']?.count || 0, color: '#4ade80' },
            { name: 'Critical', value: response['At Risk']?.count || 0, color: '#fbbf24' },
            { name: 'Delayed', value: response['Delayed']?.count || 0, color: '#ef4444' },
            { name: 'Completed', value: response['Completed']?.count || 0, color: '#3b82f6' },
          ];
          setProjectStatusData(transformedData);
        } else {
          setProjectStatusData([]);
        }
      } catch (error) {
        setProjectStatusData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProjectStatus();
  }, [selectedShip]); // Re-run when selectedShip changes

  const handleDownloadCSV = () => {
    if (projectStatusData.length === 0) return;
    const csvContent = [
      ['Status', 'Value'].join(','),
      ...projectStatusData.map(item => [item.name, item.value].join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'project_status.csv';
    link.click();
  };

  const handleDownloadPNG = () => {
    // Access the ResponsiveContainer's DOM element via ref
    const container = chartRef.current;
    if (!container) return;
    
    // Query the SVG element within the container
    const svgElement = container.querySelector('svg');
    if (!svgElement) return;

    // Rest of the download logic remains the same
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const link = document.createElement('a');
      link.download = 'project_status_chart.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const filteredData = projectStatusData;
  const total = filteredData.reduce((sum, item) => sum + item.value, 0);
  const largest = filteredData.reduce((max, item) => item.value > max.value ? item : max, { value: -1 });

  if (loading) {
    return (
      <div className="h-64 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  // Handle case where no ship is selected
  if (!selectedShip) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-500">
        Please select a ship to view project status
      </div>
    );
  }

  // Handle empty data
  if (filteredData.length === 0 || filteredData.every(item => item.value === 0)) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-500">
        No project status data available for the selected ship
      </div>
    );
  }

  return (
    <div className="h-80 relative flex items-center justify-center bg-white rounded-xl shadow border border-gray-100">
      <div className="absolute top-4 right-4 z-10 flex gap-2 bg-white p-1 rounded shadow-sm">
        <button
          onClick={handleDownloadCSV}
          className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center space-x-1 text-sm shadow-sm"
        >
          <FileText size={14} />
          <span>CSV</span>
        </button>
        <button
          onClick={handleDownloadPNG}
          className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center space-x-1 text-sm shadow-sm"
        >
          <Download size={14} />
          <span>PNG</span>
        </button>
      </div>
      <ResponsiveContainer 
        ref={chartRef}
        width="55%" 
        height="90%"
      >
        <PieChart>
          <Pie
            data={filteredData}
            cx="50%"
            cy="50%"
            innerRadius={70}
            outerRadius={100}
            paddingAngle={2}
            dataKey="value"
            nameKey="name"
            label={({ name, percent }) => percent > 0 ? `${name} ${(percent * 100).toFixed(0)}%` : ''}
            labelLine={false}
            stroke="#fff"
            strokeWidth={2}
          >
            {filteredData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip formatter={(value, name) => [`${value} (${((value/total)*100).toFixed(1)}%)`, name]} />
        </PieChart>
      </ResponsiveContainer>
      {/* Center label for largest status */}
      {largest && largest.value > 0 && (
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center select-none">
          <div className="text-3xl font-extrabold" style={{ color: largest.color }}>{largest.value}</div>
          <div className="text-lg font-semibold text-gray-700">{largest.name}</div>
        </div>
      )}
      {/* Modern legend */}
      <div className="absolute left-4 bottom-4 flex flex-col gap-2 bg-white/80 rounded p-2 shadow">
        {filteredData.map(item => (
          <div key={item.name} className="flex items-center gap-2">
            <span className="inline-block w-4 h-4 rounded-full" style={{ background: item.color }}></span>
            <span className="font-medium text-gray-700 text-sm">{item.name}</span>
            <span className="text-gray-500 text-xs">{item.value} ({((item.value/total)*100).toFixed(1)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
};